import React, { useState, useRef, useEffect } from "react";
import {
  PauseIcon,
  ClipboardDocumentListIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  ArrowPathRoundedSquareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import "./sideBar.css";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";

const SideBarKDS = ({
  handleCompletedOption,
  handleFontChange,
  fontSize = "medium",
  handlePauseOrders,
  handleRecalledItems,
  handleRedoAccept,
  handleOpenSettings,
  handleOpenMenuStock,
  handleOpenInventoryStock,
}) => {
  const [showFontOptions, setShowFontOptions] = useState(false);
  const fontBtnRef = useRef(null);
  const dropdownRef = useRef(null);
  const showSnackbar = useSnackbar();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !fontBtnRef.current.contains(event.target)
      ) {
        setShowFontOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sidebarButtons = [
    {
      label: "Inventory Stock",
      iconClass: "icon-inventory-stock",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-package sidebar-icon-svg"
          aria-hidden="true"
        >
          <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
          <path d="M12 22V12"></path>
          <polyline points="3.29 7 12 12 20.71 7"></polyline>
          <path d="m7.5 4.27 9 5.15"></path>
        </svg>
      ),
    },
    {
      label: "Completed",
      iconClass: "icon-completed",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-circle-check sidebar-icon-svg"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="m9 12 2 2 4-4"></path>
        </svg>
      ),
    },
    {
      label: "Font Change",
      iconClass: "icon-font-change",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-list-filter sidebar-icon-svg"
          aria-hidden="true"
        >
          <path d="M2 5h20"></path>
          <path d="M6 12h12"></path>
          <path d="M9 19h6"></path>
        </svg>
      ),
    },
    {
      label: "Recalled list",
      iconClass: "icon-recalled-list",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-x sidebar-icon-svg"
          aria-hidden="true"
        >
          <path d="M18 6 6 18"></path>
          <path d="m6 6 12 12"></path>
        </svg>
      ),
    },
    {
      label: "Redo Accept",
      iconClass: "icon-redo-accept",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-undo2 lucide-undo-2 sidebar-icon-svg"
          aria-hidden="true"
        >
          <path d="M9 14 4 9l5-5"></path>
          <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"></path>
        </svg>
      ),
    },
    {
      label: "Settings",
      iconClass: "icon-settings",
      svg: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-settings-2 sidebar-icon-svg"
          aria-hidden="true"
        >
          <path d="M14 17H5"></path>
          <path d="M19 7h-9"></path>
          <circle cx="17" cy="17" r="3"></circle>
          <circle cx="7" cy="7" r="3"></circle>
        </svg>
      ),
    },
  ];

  const handleButtonClick = (label) => {
    if (label === "Completed") handleCompletedOption();
    if (label === "Font Change") setShowFontOptions((prev) => !prev);
    if (label === "Pause Online Order") showSnackbar("Feature coming soon", "warning");
    if (label === "Recalled list") handleRecalledItems();
    if (label === "Redo Accept") handleRedoAccept();
    if (label === "Settings") handleOpenSettings();
    if (label === "Menu Stock") handleOpenMenuStock();
    if (label === "Inventory Stock") handleOpenInventoryStock();
  };

  return (
    <div className="kds-right-sidebar">
      {sidebarButtons.map((btn, index) => (
        <React.Fragment key={index}>
          <div className="sidebar-btn-wrapper">
            <button
              className="sidebar-btn"
              onClick={() => handleButtonClick(btn?.label)}
              ref={btn?.label === "Font Change" ? fontBtnRef : null}
            >
              <div className={`sidebar-icon-container ${btn?.iconClass}`}>{btn?.svg}</div>
              <div className="sidebar-label-wrapper">
                <span className="sidebar-label">{btn?.label}</span>
              </div>
            </button>

            {/* Absolutely positioned dropdown (not wrapped) */}
            {btn.label === "Font Change" && showFontOptions && (
              <div className="font-options-dropdown" ref={dropdownRef}>
                {[["small", "Small"], ["medium", "Medium"], ["large", "Large"]].map(([size, label]) => (
                  <button
                    key={size}
                    type="button"
                    className={fontSize === size ? "is-selected" : undefined}
                    aria-pressed={fontSize === size}
                    onClick={() => handleFontChange(size)}
                  >
                    {label}
                    {fontSize === size && (
                      <svg viewBox="0 0 16 16" aria-hidden="true" fill="none">
                        <path d="m3 8 3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default SideBarKDS;
